import {LocalStorage} from '../../storage';
import {
  AUTH_INIT_MODELS,
  HOST_ID_KEY,
  HOUSING_ID_KEY,
  RESERVATION_ID_KEY,
  SHORT_LINK_INSTANCE,
  SHORT_LINK_TYPE,
} from './constants';
import type {OldShortLink, ShortLink} from './types';
import type {RESERVATION_SEARCH_METHOD} from '../reservation';

export type ShortLinkResponseData = OldShortLink | ShortLink;

export type ObjectDetails = {
  id?: string;
  model?: string;
  link?: string;
  redirectTo?: string;
  searchMode?: string;
  reservationSearchMethods?: RESERVATION_SEARCH_METHOD[];
  housingSearchEnabled?: boolean;
  accountName?: string;
};

export type ResolvedShortLinkIds = {
  reservationId?: string;
  housingId?: string;
  userId?: string;
  isNewShortLink: boolean;
};

const DEFAULT_OBJECT_DETAILS: ObjectDetails = {
  id: '',
  model: '',
  link: '',
  redirectTo: '',
  searchMode: '',
  reservationSearchMethods: undefined,
  housingSearchEnabled: false,
};

const SHORT_LINK_MAP: Partial<Record<SHORT_LINK_INSTANCE, AUTH_INIT_MODELS>> = {
  [SHORT_LINK_INSTANCE.housing]: AUTH_INIT_MODELS.housing,
  [SHORT_LINK_INSTANCE.reservation]: AUTH_INIT_MODELS.reservation,
  [SHORT_LINK_INSTANCE.unlockLink]: AUTH_INIT_MODELS.reservation,
};

function isNewShortLink(data?: ShortLinkResponseData): data is ShortLink {
  return Boolean((data as ShortLink)?.instance_id);
}

function isShortLink(data?: ShortLinkResponseData): data is OldShortLink | ShortLink {
  return Boolean(
    (data as OldShortLink)?.type ||
      (data as OldShortLink)?.object_id ||
      (data as ShortLink)?.instance_id,
  );
}

function resolveObjectDetails(data: ShortLinkResponseData | undefined): ObjectDetails {
  if (isShortLink(data) && !isNewShortLink(data)) {
    return {
      id: data?.object_id,
      model: data?.model_name,
      link: data?.link,
      redirectTo: data?.url_path,
      searchMode: data?.reservation_search_mode,
      reservationSearchMethods: data?.reservation_search_methods,
      housingSearchEnabled: data?.housing_search_enabled ?? false,
      accountName: data?.account_name || undefined,
    };
  }

  if (isShortLink(data) && isNewShortLink(data)) {
    return {
      id: data.instance_id,
      model: SHORT_LINK_MAP[data.instance_class] || '',
      link: '',
      redirectTo: '',
      searchMode: '',
      reservationSearchMethods: undefined,
      housingSearchEnabled: false,
    };
  }

  return DEFAULT_OBJECT_DETAILS;
}

function resolveLinkType(data?: ShortLinkResponseData): SHORT_LINK_TYPE | undefined {
  if (!isShortLink(data)) return undefined;
  if (data.type) return data.type;

  const {model} = resolveObjectDetails(data);
  const isHousingOrUser =
    model === AUTH_INIT_MODELS.housing || model === AUTH_INIT_MODELS.user;
  return isHousingOrUser ? SHORT_LINK_TYPE.housingCheckinOnline : undefined;
}

function getShortLinkId(criteria: SHORT_LINK_INSTANCE, data?: ShortLink) {
  if (data?.instance_class === criteria) {
    return data.instance_id;
  }
  return undefined;
}

function resolveShortLinkIds(data?: ShortLinkResponseData): ResolvedShortLinkIds {
  if (!isShortLink(data)) {
    return {isNewShortLink: false};
  }

  if (isNewShortLink(data)) {
    return {
      isNewShortLink: true,
      reservationId: getShortLinkId(SHORT_LINK_INSTANCE.reservation, data),
      housingId: getShortLinkId(SHORT_LINK_INSTANCE.housing, data),
    };
  }

  return {
    isNewShortLink: false,
    reservationId:
      data?.model_name === AUTH_INIT_MODELS.reservation ? data?.object_id : undefined,
    housingId:
      data?.model_name === AUTH_INIT_MODELS.housing ? data?.object_id : undefined,
    userId: data?.model_name === AUTH_INIT_MODELS.user ? data?.object_id : undefined,
  };
}

function persistResolvedIds({
  reservationId,
  housingId,
  userId,
  isNewShortLink,
}: ResolvedShortLinkIds) {
  const resolvedHousingId = isNewShortLink
    ? housingId
    : LocalStorage.get(HOUSING_ID_KEY) || housingId;
  const resolvedUserId = isNewShortLink
    ? userId
    : LocalStorage.get(HOST_ID_KEY) || userId;

  LocalStorage.set(RESERVATION_ID_KEY, reservationId);
  LocalStorage.set(HOUSING_ID_KEY, resolvedHousingId);
  LocalStorage.set(HOST_ID_KEY, resolvedUserId);
}

export {
  isNewShortLink,
  persistResolvedIds,
  isShortLink,
  resolveLinkType,
  resolveObjectDetails,
  resolveShortLinkIds,
};
